/**
 * Wallet — Central Currency & Asset Store for Fugazi and Tickets.
 * 
 * Rules:
 * - "Fugazi" has NO plural.
 * - Initial balance for every new commander is 300 Fugazi.
 * - Persisted via StorageAdapter.
 * - Fully decoupled from lockstep netcode simulation.
 */

import { storageAdapter } from './storageAdapter.js';
import { progress } from './progress.js';

export const INITIAL_FUGAZI_BALANCE = 300;
export const INITIAL_TICKETS_COUNT = 5;

// D pays out 0 (Anti-abuse protection against instant-restart and AFK farming).
// C pays out a fair baseline (0.5x) so honest efforts are always rewarded.
// Granular 9-step progression: B..S+ rewards tactical skill and decisive mastery.
export const GAMERANK_REWARD_MULTIPLIERS = {
  'D': 0,
  'C': 0.5,
  'B': 1.0,
  'B+': 1.35,
  'A': 1.75,
  'A+': 2.25,
  'S-': 2.85,
  'S': 3.5,
  'S+': 4.5
};

export const MATCH_BASE_REWARD = {
  victory: 40,
  defeat: 20
};

class WalletManager {
  constructor() {
    this._listeners = new Set();
    this._state = this._load();
    this._batchDepth = 0;
    this._batchDirty = false;
    this._notifyPending = false;
  }

  reload() {
    this._state = this._load();
    this._notify();
  }

  _load() {
    const raw = storageAdapter.get('wallet', null);
    if (!raw || typeof raw.fugazi !== 'number') {
      const initial = {
        fugazi: INITIAL_FUGAZI_BALANCE,
        tickets: INITIAL_TICKETS_COUNT,
        version: 3,
        history: []
      };
      storageAdapter.set('wallet', initial);
      return initial;
    }

    let dirty = false;

    // Migration to version 2 (Initial Fugazi balance update to 300)
    if (!raw.version || raw.version < 2) {
      if (raw.fugazi < INITIAL_FUGAZI_BALANCE) {
        raw.fugazi = INITIAL_FUGAZI_BALANCE;
      }
      dirty = true;
    }

    // Migration to version 3 (Initial tickets default to 5)
    if (!raw.version || raw.version < 3) {
      if (typeof raw.tickets !== 'number') {
        raw.tickets = INITIAL_TICKETS_COUNT;
      } else {
        raw.tickets = Math.max(raw.tickets, INITIAL_TICKETS_COUNT);
      }
      dirty = true;
    }

    if (dirty) {
      raw.version = 3;
      storageAdapter.set('wallet', raw);
    }

    return raw;
  }

  _save() {
    if (this._batchDepth > 0) {
      this._batchDirty = true;
      return;
    }
    storageAdapter.set('wallet', this._state);
    this._notifyPending = false;
    this._notify();
  }

  /**
   * Coalesces every mutation between beginBatch() and endBatch() into ONE storage write and one
   * notification (a pack opening used to do up to ~10 synchronous JSON.stringify + localStorage
   * writes and listener fan-outs on the first frame of the tear animation). The write itself is
   * never deferred beyond endBatch(), only the listener fan-out can be (endBatch({ notify: false })
   * + flushNotify()), so a reload mid-animation can neither refund nor duplicate anything.
   */
  beginBatch() {
    this._batchDepth++;
  }

  endBatch({ notify = true } = {}) {
    if (this._batchDepth === 0) return;
    if (--this._batchDepth > 0 || !this._batchDirty) return;
    this._batchDirty = false;
    storageAdapter.set('wallet', this._state);
    if (notify) {
      this._notifyPending = false;
      this._notify();
    } else {
      this._notifyPending = true;
    }
  }

  flushNotify() {
    if (!this._notifyPending) return;
    this._notifyPending = false;
    this._notify();
  }

  _notify(changeDetail = {}) {
    for (const listener of this._listeners) {
      try {
        listener(this.getState(), changeDetail);
      } catch (err) {
        console.error('[WalletManager] Listener error:', err);
      }
    }
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      try {
        window.dispatchEvent(new CustomEvent('ucg:wallet:changed', {
          detail: { state: this.getState(), change: changeDetail }
        }));
      } catch {}
    }
  }

  getState() {
    return {
      fugazi: this._state.fugazi,
      tickets: this._state.tickets
    };
  }

  getFugazi() {
    return this._state.fugazi;
  }

  getTickets() {
    return this._state.tickets;
  }

  addFugazi(amount, reason = 'match') {
    const qty = Math.max(0, Math.floor(amount));
    if (qty === 0) return this._state.fugazi;

    this._state.fugazi += qty;
    this._save();
    // Kennzahl C (docs/XP_PROGRESSION_PLAN.md section 4) needs a
    // never-decreasing "lifetime earned" counter - the current balance
    // above shrinks on spendFugazi(), so it can't serve that purpose.
    // Hooked at this single choke point rather than at each call site so
    // no future earning path can be added without also being counted.
    progress.recordFugaziEarned(qty);
    return this._state.fugazi;
  }

  spendFugazi(amount, reason = 'purchase') {
    const cost = Math.max(0, Math.floor(amount));
    if (this._state.fugazi < cost) {
      return false;
    }

    this._state.fugazi -= cost;
    this._save();
    return true;
  }

  addTickets(count = 1, reason = 'pack_drop') {
    const qty = Math.max(0, Math.floor(count));
    if (qty === 0) return this._state.tickets;

    this._state.tickets += qty;
    this._save();
    return this._state.tickets;
  }

  spendTicket() {
    return this.spendTickets(1);
  }

  spendTickets(count = 1) {
    const qty = Math.max(1, Math.floor(count));
    if (this._state.tickets < qty) {
      return false;
    }

    this._state.tickets -= qty;
    this._save();
    return true;
  }

  resetWallet(initialBalance = INITIAL_FUGAZI_BALANCE, initialTickets = 0) {
    this._state = {
      fugazi: Math.max(0, Math.floor(initialBalance)),
      tickets: Math.max(0, Math.floor(initialTickets)),
      version: 3,
      history: []
    };
    this._save();
    return this.getState();
  }

  addListener(listener) {
    if (typeof listener === 'function') {
      this._listeners.add(listener);
    }
    return () => this._listeners.delete(listener);
  }

  /**
   * Calculates post-match Fugazi reward based on match outcome, gamerank, duration and mode.
   * @param {boolean} isVictory
   * @param {string} grade - 'D', 'C', 'B', 'A', 'S', or 'S+'
   * @param {{ durationSeconds?: number, isOnline?: boolean }} [options={}]
   * @returns {{ base: number, multiplier: number, perfReward: number, timeBonus: number, onlineMultiplier: number, isOnline: boolean, total: number, grade: string }}
   */
  calculateMatchReward(isVictory, grade = 'C', options = {}) {
    const cleanGrade = (grade || 'C').toUpperCase();
    const isOnline = Boolean(options.isOnline);
    const durationSeconds = Math.max(0, Number(options.durationSeconds) || 0);

    // Rank D is an anti-abuse tier: always yields strictly 0 Fugazi
    if (cleanGrade === 'D') {
      return {
        base: 0,
        multiplier: 0,
        perfReward: 0,
        timeBonus: 0,
        onlineMultiplier: isOnline ? 1.5 : 1.0,
        isOnline,
        total: 0,
        grade: 'D'
      };
    }

    const base = isVictory ? MATCH_BASE_REWARD.victory : MATCH_BASE_REWARD.defeat;
    const multiplier = Object.prototype.hasOwnProperty.call(GAMERANK_REWARD_MULTIPLIERS, cleanGrade)
      ? GAMERANK_REWARD_MULTIPLIERS[cleanGrade]
      : 1.0;

    const perfReward = Math.round(base * multiplier);

    // Time / Battle Endurance bonus: +3 Fugazi per full minute (starting after minute 1),
    // capped at 10 minutes (600s, max +27 Fugazi) to prevent roping/stalling
    const effectiveMinutes = Math.floor(Math.min(600, Math.max(0, durationSeconds - 60)) / 60);
    const timeBonus = effectiveMinutes * 3;

    // Online PvP Honor Multiplier: 1.5x on the full match settlement
    const onlineMultiplier = isOnline ? 1.5 : 1.0;

    const total = Math.round((perfReward + timeBonus) * onlineMultiplier);

    return {
      base,
      multiplier,
      perfReward,
      timeBonus,
      onlineMultiplier,
      isOnline,
      total,
      grade: cleanGrade
    };
  }
}

export const wallet = new WalletManager();
export { WalletManager };
